import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useSite } from "@/lib/site";

const KEY = "c4x4_consent";
export const readConsent = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } };
const read = readConsent;

export const openCookieSettings = () => window.dispatchEvent(new Event("c4x4:cookies"));

function loadOptional(consent, integrations) {
  if (consent?.analytics && integrations?.ga4_id && !window.gtag) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${integrations.ga4_id}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", integrations.ga4_id);
  }
  // Meta Pixel loads here once integrations.meta_pixel_id is configured and marketing consent is given.
}

export default function CookieConsent() {
  const site = useSite();
  const [consent, setConsent] = useState(read);
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (!consent) { const t = setTimeout(() => setOpen(true), 1400); return () => clearTimeout(t); }
    loadOptional(consent, site.integrations);
    return undefined;
  }, [consent, site.integrations]);

  useEffect(() => {
    const h = () => { setPrefs({ analytics: !!consent?.analytics, marketing: !!consent?.marketing }); setManage(true); setOpen(true); };
    window.addEventListener("c4x4:cookies", h);
    return () => window.removeEventListener("c4x4:cookies", h);
  }, [consent]);

  const save = (c) => {
    const v = { necessary: true, ...c, date: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(v));
    window.dispatchEvent(new Event("c4x4:consent"));
    setConsent(v);
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div role="dialog" aria-label="Cookie preferences" data-testid="cookie-banner"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }} transition={{ duration: 0.5 }}
          className="fixed bottom-4 left-4 right-4 z-[70] max-w-md border border-stone bg-paper p-6 shadow-[0_20px_60px_-20px_rgba(22,53,44,.35)] sm:right-auto">
          <p className="font-serif text-2xl text-forest">Cookies</p>
          <p className="mt-2 text-sm leading-relaxed text-charcoal/80">
            We use necessary cookies to run this site. With your permission we would also like to use analytics and marketing cookies. See our <Link to="/legal/cookies" className="underline">Cookie Policy</Link>.
          </p>
          {manage && (
            <div className="mt-4 space-y-3 border-t border-stone pt-4 text-sm">
              <label className="flex items-center justify-between"><span>Necessary</span><span className="text-charcoal/70">Always on</span></label>
              {["analytics", "marketing"].map((k) => (
                <label key={k} className="flex cursor-pointer items-center justify-between capitalize">
                  <span>{k}</span>
                  <input type="checkbox" data-testid={`cookie-${k}-toggle`} checked={prefs[k]} onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })} className="h-4 w-4 accent-[#16352C]" />
                </label>
              ))}
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            {manage ? (
              <button className="btn btn-primary !py-3" data-testid="cookie-save" onClick={() => save(prefs)}>Save preferences</button>
            ) : (
              <>
                <button className="btn btn-primary !py-3" data-testid="cookie-accept-all" onClick={() => save({ analytics: true, marketing: true })}>Accept all</button>
                <button className="btn btn-secondary !py-3" data-testid="cookie-reject" onClick={() => save({ analytics: false, marketing: false })}>Necessary only</button>
                <button className="link-u px-2 text-sm" data-testid="cookie-manage" onClick={() => setManage(true)}>Manage</button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
